<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useScrollSpy } from '@/composables/useScrollSpy'
import { useAuth } from '@/composables/useAuth'
import { hasAdminRole } from '@/types/auth'
import type { NavItem } from '@/types/landing'
import UserAvatar from '@/components/common/UserAvatar.vue'

interface Props {
  navItems: NavItem[]
}

const props = defineProps<Props>()
const router = useRouter()
const { state, logout } = useAuth()

const isAuthenticated = computed(() => state.isAuthenticated)
const isAdmin = computed(() => hasAdminRole(state.currentUser?.role))
const userName = computed(() => state.currentUser?.displayName ?? '')
const userAvatarUrl = computed(() => state.currentUser?.avatarUrl || '')

const { isScrolled } = useScrollSpy(100)
const isMobileMenuOpen = ref(false)
const mobileMenuRef = ref<HTMLElement | null>(null)
const hamburgerRef = ref<HTMLButtonElement | null>(null)

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false
}

function scrollToAnchor(anchor: string) {
  closeMobileMenu()
  const el = document.querySelector(anchor)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
}

function goToLogin() {
  router.push('/login')
}

function goToWorkspace() {
  router.push(isAdmin.value ? '/admin' : '/dashboard')
}

async function handleLogout() {
  await logout()
  // 登出后跳转登录页，与应用内退出行为保持一致
  router.push('/login')
}

// Escape 键关闭菜单
function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isMobileMenuOpen.value) {
    closeMobileMenu()
  }
}

// 焦点锁定在菜单内
watch(isMobileMenuOpen, async (isOpen) => {
  if (isOpen) {
    await nextTick()
    const focusable = mobileMenuRef.value?.querySelectorAll<HTMLElement>(
      'a, button, [tabindex]:not([tabindex="-1"])',
    )
    focusable?.[0]?.focus()
    document.addEventListener('keydown', handleKeydown)
    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', handleKeydown)
    document.body.style.overflow = ''
    hamburgerRef.value?.focus()
  }
})
</script>

<template>
  <header
    class="navbar fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out"
    :class="[
      isScrolled
        ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
        : 'bg-transparent',
    ]"
  >
    <nav
      class="mx-auto flex h-16 max-w-[1200px] items-center justify-between px-6"
      aria-label="主导航"
    >
      <!-- Logo -->
      <a
        href="#hero"
        class="flex items-center gap-2.5 text-xl font-bold no-underline transition-colors duration-300"
        :class="isScrolled ? 'text-[var(--color-title)]' : 'text-white'"
      >
        <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#3B5BDB] to-[#7048E8] text-sm font-bold text-white shadow-sm">智</span>
        <span class="tracking-tight">智台账</span>
      </a>

      <!-- 桌面导航 -->
      <div class="hidden items-center gap-8 md:flex">
        <ul class="flex items-center gap-6 list-none m-0 p-0">
          <li v-for="item in props.navItems" :key="item.anchor">
            <button
              class="relative cursor-pointer border-none bg-transparent px-1 py-2 text-sm font-medium transition-colors duration-200 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-[#3B5BDB] after:transition-all after:duration-200 hover:after:w-full"
              :class="isScrolled ? 'text-[var(--color-body)] hover:text-[#3B5BDB]' : 'text-white/70 hover:text-white'"
              @click="scrollToAnchor(item.anchor)"
            >
              {{ item.label }}
            </button>
          </li>
        </ul>

        <div v-if="isAuthenticated" class="flex items-center gap-3">
          <ElDropdown trigger="click">
            <div
              class="flex cursor-pointer items-center gap-2 rounded-full px-3 py-1.5 transition-colors duration-200"
              :class="isScrolled ? 'bg-[var(--color-card-bg)] hover:bg-[var(--color-divider)]' : 'bg-white/10 hover:bg-white/15'"
            >
              <div
                class="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-xs font-semibold bg-gradient-to-br from-[#3B5BDB] to-[#7048E8] text-white"
              >
                <UserAvatar :src="userAvatarUrl">
                  {{ userName.charAt(0) }}
                </UserAvatar>
              </div>
              <span class="hidden text-sm sm:block" :class="isScrolled ? 'text-[var(--color-title)]' : 'text-white'">{{ userName }}</span>
            </div>
            <template #dropdown>
              <ElDropdownMenu>
                <ElDropdownItem @click="goToWorkspace">
                  {{ isAdmin ? '管理后台' : '工作台' }}
                </ElDropdownItem>
                <ElDropdownItem @click="router.push('/settings')">
                  账号设置
                </ElDropdownItem>
                <ElDropdownItem divided @click="handleLogout">
                  退出登录
                </ElDropdownItem>
              </ElDropdownMenu>
            </template>
          </ElDropdown>
        </div>
        <div v-else class="flex items-center gap-3">
          <button
            class="rounded-full px-5 py-2 text-sm font-medium transition-all duration-200"
            :class="isScrolled ? 'border border-[#3B5BDB] text-[#3B5BDB] hover:bg-[#3B5BDB] hover:text-white' : 'border border-white/30 text-white/80 hover:bg-white/10 hover:border-white/50'"
            @click="goToLogin"
          >
            登录
          </button>
          <button
            class="rounded-full bg-gradient-to-r from-[#3B5BDB] to-[#7048E8] px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg hover:shadow-[#3B5BDB]/25"
            @click="goToLogin"
          >
            免费试用
          </button>
        </div>
      </div>

      <!-- 移动端汉堡按钮 -->
      <button
        ref="hamburgerRef"
        class="flex h-10 w-10 items-center justify-center rounded-lg border-none bg-transparent md:hidden"
        :class="isScrolled ? 'text-[var(--color-title)]' : 'text-white'"
        :aria-expanded="isMobileMenuOpen"
        aria-controls="mobile-menu"
        aria-label="打开导航菜单"
        @click="toggleMobileMenu"
      >
        <svg
          v-if="!isMobileMenuOpen"
          xmlns="http://www.w3.org/2000/svg"
          width="24" height="24" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="24" height="24" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </nav>

    <!-- 移动端全屏菜单 -->
    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-300 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div
        v-if="isMobileMenuOpen"
        id="mobile-menu"
        ref="mobileMenuRef"
        class="fixed inset-0 z-50 flex flex-col bg-white pt-16 md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="导航菜单"
      >
        <button
          class="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-lg text-[var(--color-title)]"
          aria-label="关闭菜单"
          @click="closeMobileMenu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <ul class="flex flex-col gap-2 p-6 list-none">
          <li v-for="item in props.navItems" :key="item.anchor">
            <button
              class="w-full rounded-xl border-none bg-transparent px-4 py-3 text-left text-lg font-medium text-[var(--color-title)] transition-colors hover:bg-[var(--color-card-bg)]"
              @click="scrollToAnchor(item.anchor)"
            >
              {{ item.label }}
            </button>
          </li>
        </ul>

        <div v-if="isAuthenticated" class="mt-auto flex flex-col gap-3 p-6">
          <div class="flex items-center gap-3 px-4 py-3">
            <div class="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#3B5BDB] to-[#7048E8] text-sm font-semibold text-white">
              <UserAvatar :src="userAvatarUrl">
                {{ userName.charAt(0) }}
              </UserAvatar>
            </div>
            <span class="text-base font-medium text-[var(--color-title)]">{{ userName }}</span>
          </div>
          <button
            class="w-full rounded-full bg-gradient-to-r from-[#3B5BDB] to-[#7048E8] px-5 py-3 text-sm font-semibold text-white"
            @click="goToWorkspace"
          >
            {{ isAdmin ? '进入管理后台' : '进入工作台' }}
          </button>
          <button
            class="w-full rounded-full border border-[var(--color-divider)] px-5 py-3 text-sm font-medium text-[var(--color-body)]"
            @click="handleLogout"
          >
            退出登录
          </button>
        </div>
        <div v-else class="mt-auto flex flex-col gap-3 p-6">
          <button
            class="w-full rounded-full border border-[#3B5BDB] px-5 py-3 text-sm font-medium text-[#3B5BDB]"
            @click="goToLogin"
          >
            登录
          </button>
          <button
            class="w-full rounded-full bg-gradient-to-r from-[#3B5BDB] to-[#7048E8] px-5 py-3 text-sm font-semibold text-white"
            @click="goToLogin"
          >
            免费试用
          </button>
        </div>
      </div>
    </Transition>
  </header>
</template>
